/* eslint-disable @typescript-eslint/no-explicit-any */
import FlatsyButtonsField from '@flatsy/core/Form/ReactFormHooks/Field/Buttons'
import FlatsyTextField from '@flatsy/core/Form/ReactFormHooks/Field/Text'
import { TextError } from '@flatsy/core/Form/ReactFormHooks/Field/TextError'
import Separator from '@flatsy/core/Layout/Separator'
import { LabelSeparator } from '@flatsy/core/MaterialUi/Typo'
import { FormValue } from '@flatsy/types/Applications'
import i18n from '@flatsy/utils/translations'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from '@material-ui/core'
import React, { useContext, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useApplicantById } from 'src/hooks/ApplicantsSelector'
import * as yup from 'yup'
import {
  askActivtyDuration,
  askBenefitsDuration,
  askContractDuration,
  askDuration,
  askTrialPeriod,
  isEmployed,
  isUnemployed,
  isWithoutActivity,
} from '../../../../utils/RenderingQualificationField'
import { FormContext, ReturnContextProps } from '../../Context'
import { useFormStyles } from '../Style'
import { SharedProps } from '../Type'
import { ProfessionalData } from './Type'
import {
  renderActivity,
  renderLabelDuration,
  renderLabelRevenu,
  renderLabelRevenuReference,
  renderLabelUnemployement,
  renderPeriod,
  renderProSituation,
  renderProSituationGarant,
  renderProStatus,
  renderSituation,
  renderTrialOrEmployed,
  renderUnemployment,
} from './Utils'

const Professional: React.FC<SharedProps & { isGuarantor?: boolean }> = ({
  renderMobileFormButton,
  isGuarantor,
}) => {
  const formClasses = useFormStyles()
  const {
    wizardContext: { context },
    saveApplicantAndContinue,
    includeBenefits,
    isPinel,
  } = useContext(FormContext) as ReturnContextProps
  const applicant = useApplicantById(context.applicantId)
  const schema = yup.object({
    duration: yup
      .string()
      .nullable()
      .when(['professional_situation', 'situation'], {
        is: (valPro: string, valSituation: string) => {
          if (askContractDuration(valSituation) && askDuration(valPro)) {
            return true
          }
          if (askActivtyDuration(valSituation) && askDuration(valPro)) {
            return true
          }
          if (askBenefitsDuration(valSituation) && askDuration(valPro)) {
            return true
          }
          return false
        },
        then: yup.string().required().typeError(TextError.empty),
      }),
    professional_situation: yup.string().required().typeError(TextError.empty),
    situation: yup.string().required().typeError(TextError.empty),
    revenue: yup.string().required().typeError(TextError.empty),
    revenue_reference: yup
      .string()
      .nullable()
      .when('professional_situation', {
        is: isPinel,
        then: yup.string().required().typeError(TextError.empty),
      }),
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { isSubmitSuccessful, isValid },
  } = useForm<ProfessionalData>({
    mode: 'all',
    resolver: yupResolver(schema),
  })

  const professional_situation = watch('professional_situation')
  const situation = watch('situation')

  useEffect(() => {
    if (applicant) {
      reset({
        duration: applicant?.duration || '',
        professional_situation: applicant?.professional_situation || '',
        situation: applicant?.situation || '',
        revenue: applicant?.revenue || '',
        revenue_reference: applicant?.revenue_reference || '',
      })
    }
  }, [applicant])

  useEffect(() => {
    if (professional_situation === FormValue.proSituation.RETIRED) {
      setValue('situation', FormValue.proSituation.RETIRED)
    } else if (professional_situation === FormValue.proSituation.STUDENT) {
      setValue('situation', FormValue.proSituation.STUDENT)
    } else {
      setValue('situation', undefined)
    }
  }, [professional_situation])

  useEffect(() => {
    const TriggerValidation = async () => {
      await trigger(['situation', 'professional_situation'])
    }
    TriggerValidation()
  }, [professional_situation, situation])

  const onSubmit = async (values: ProfessionalData): Promise<undefined> => {
    if (applicant) {
      saveApplicantAndContinue(applicant, values)
    }
    return undefined
  }

  if (!applicant) {
    return null
  }

  return (
    <div className={formClasses.container}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <LabelSeparator className={formClasses.smallMarginLabel}>
          {renderSituation(applicant)}
        </LabelSeparator>
        <Controller
          name="professional_situation"
          control={control}
          render={({ field, fieldState }) => (
            <FlatsyButtonsField
              {...field}
              {...fieldState}
              error={undefined}
              isSubmitSuccessful={isSubmitSuccessful}
              options={!isGuarantor ? renderProSituation() : renderProSituationGarant()}
            />
          )}
        />

        {isEmployed(professional_situation) && (
          <>
            <LabelSeparator className={formClasses.smallMarginLabel}>
              {renderActivity(applicant)}
            </LabelSeparator>
            <Controller
              name="situation"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyButtonsField
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                  error={undefined}
                  options={
                    situation === FormValue.proStatus.CDI_PROBATION
                      ? renderProStatus(FormValue.proStatus.CDI_PROBATION)
                      : renderProStatus(FormValue.proSituation.EMPLOYED)
                  }
                />
              )}
            />
          </>
        )}

        {isUnemployed(professional_situation) && (
          <>
            <LabelSeparator className={formClasses.smallMarginLabel}>
              {renderActivity(applicant)}
            </LabelSeparator>
            <Controller
              name="situation"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyButtonsField
                  {...field}
                  {...fieldState}
                  error={undefined}
                  isSubmitSuccessful={isSubmitSuccessful}
                  options={renderProStatus(FormValue.proSituation.UNEMPLOYED)}
                />
              )}
            />
          </>
        )}
        {isWithoutActivity(professional_situation) && (
          <>
            <LabelSeparator className={formClasses.smallMarginLabel}>
              {renderUnemployment(applicant)}
            </LabelSeparator>
            <Controller
              name="situation"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyButtonsField
                  {...field}
                  {...fieldState}
                  error={undefined}
                  isSubmitSuccessful={isSubmitSuccessful}
                  options={renderProStatus(FormValue.proSituation.WITHOUT_ACTIVITY)}
                />
              )}
            />
          </>
        )}

        {askTrialPeriod(situation) && isEmployed(professional_situation) && (
          <>
            <LabelSeparator className={formClasses.smallMarginLabel}>
              {renderPeriod(applicant)}
            </LabelSeparator>
            <Controller
              name="situation"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyButtonsField
                  {...field}
                  {...fieldState}
                  error={undefined}
                  isSubmitSuccessful={isSubmitSuccessful}
                  options={renderTrialOrEmployed()}
                />
              )}
            />
          </>
        )}
        {askContractDuration(situation) && askDuration(professional_situation) && (
          <>
            <Controller
              name="duration"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyTextField
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                  type="number"
                  inputAdornment={i18n.t('months').toLowerCase()}
                  label={renderLabelDuration(applicant)}
                />
              )}
            />
            <Separator size="24px" />
          </>
        )}
        {askActivtyDuration(situation) && askDuration(professional_situation) && (
          <>
            <Controller
              name="duration"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyTextField
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                  type="number"
                  inputAdornment={i18n.t('months').toLowerCase()}
                  label={i18n.t('job_duration')}
                />
              )}
            />
            <Separator size="24px" />
          </>
        )}
        {askBenefitsDuration(situation) && askDuration(professional_situation) && (
          <>
            <Controller
              name="duration"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyTextField
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                  type="number"
                  inputAdornment={i18n.t('months').toLowerCase()}
                  label={renderLabelUnemployement(applicant)}
                />
              )}
            />
            <Separator size="24px" />
          </>
        )}

        <Controller
          name="revenue"
          control={control}
          render={({ field, fieldState }) => (
            <FlatsyTextField
              {...field}
              {...fieldState}
              isSubmitSuccessful={isSubmitSuccessful}
              type="number"
              inputAdornment={i18n.t('@€')}
              label={renderLabelRevenu(applicant)}
              helperText={
                includeBenefits
                  ? i18n.t('include_any_financial_aid')
                  : i18n.t('do_not_include_any_financial_aid')
              }
            />
          )}
        />
        {isPinel && (
          <>
            <Separator size="24px" />
            <Controller
              name="revenue_reference"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyTextField
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                  type="number"
                  inputAdornment={i18n.t('@€')}
                  label={renderLabelRevenuReference(applicant)}
                  helperText={i18n.t('taxes_amount_on_the_previous_year_income').toLowerCase()}
                />
              )}
            />
          </>
        )}

        {!renderMobileFormButton ? (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            type="submit"
            className={formClasses.submit}
            disabled={!isValid}
          >
            {i18n.t('continue')}
          </Button>
        ) : (
          renderMobileFormButton(isValid, !isValid)
        )}
      </form>
    </div>
  )
}

export default Professional
